// Single source of truth for the dashboard navigation tree.
// Consumed by app/components/NavBar.tsx (render) and the nav-template editor (picker).

export type NavItem = { href: string; label: string };
export type NavSection = { heading: string; items: NavItem[] };
export type DirectLink = { kind: 'link'; href: string; label: string };
export type FlatGroup = { kind: 'flat'; label: string; items: NavItem[] };
export type SectionedGroup = { kind: 'sectioned'; label: string; sections: NavSection[] };
export type NavEntry = DirectLink | FlatGroup | SectionedGroup;

export const nav: NavEntry[] = [
  { kind: 'link', href: '/dashboard', label: 'Übersicht' },
  {
    kind: 'sectioned',
    label: 'Analyse',
    sections: [
      {
        heading: 'Shop & Traffic',
        items: [
          { href: '/dashboard/shopify', label: 'Shopify' },
          { href: '/dashboard/attribution', label: 'Attribution' },
          { href: '/dashboard/channels', label: 'Channels' },
          { href: '/dashboard/channels/roi', label: 'Channel-ROI' },
          { href: '/dashboard/sessions', label: 'Sessions' },
          { href: '/dashboard/events', label: 'Events' },
          { href: '/dashboard/landingpages', label: 'Landingpages' },
          { href: '/dashboard/sections', label: 'Sections' },
        ],
      },
      {
        heading: 'Conversion',
        items: [
          { href: '/dashboard/notarzt', label: 'Notarzt' },
          { href: '/dashboard/conversion-diagnose', label: 'Conversion-Diagnose' },
          { href: '/dashboard/conversion-funnel', label: 'Funnel' },
          { href: '/dashboard/lifecycle', label: 'Lifecycle' },
          { href: '/dashboard/recovery', label: 'Cart Recovery' },
        ],
      },
      {
        heading: 'Ads & Feedback',
        items: [
          { href: '/dashboard/social-analytics', label: 'Social Analytics' },
          { href: '/dashboard/meta-ads', label: 'Meta Ads' },
          { href: '/dashboard/meta-ads/organic', label: 'Instagram Organic' },
          { href: '/dashboard/voice', label: 'Customer Voice' },
          { href: '/dashboard/umfrage-ergebnisse', label: 'Umfrage-Ergebnisse' },
        ],
      },
      {
        heading: 'Finanzen',
        items: [{ href: '/dashboard/kosten', label: 'Kosten' }],
      },
    ],
  },
  {
    kind: 'sectioned',
    label: 'Meta Ads',
    sections: [
      {
        heading: 'Performance',
        items: [
          { href: '/dashboard/meta-ads', label: 'Performance' },
          { href: '/dashboard/ads/stats', label: 'Ads Stats' },
          { href: '/dashboard/meta-ads/organic', label: 'Instagram Organic' },
        ],
      },
      {
        heading: 'Creative-Produktion',
        items: [
          { href: '/dashboard/ads/creative-briefs', label: 'Creative Briefs' },
          { href: '/dashboard/ads/creatives', label: 'Ad-Creatives' },
          { href: '/dashboard/ads/angles', label: 'Angle-Katalog' },
          { href: '/dashboard/ads/plan-drafts', label: 'Plan-Drafts' },
        ],
      },
      {
        heading: 'Strategie',
        items: [
          { href: '/dashboard/content-machine/ads-proposals', label: 'Ads Proposals' },
        ],
      },
    ],
  },
  {
    kind: 'sectioned',
    label: 'Shopify Optimierung',
    sections: [
      {
        heading: 'Analyse',
        items: [
          { href: '/dashboard/page-optimizer/pages', label: 'Seiten-Übersicht' },
          { href: '/dashboard/page-optimizer/funnel-map', label: 'Funnel Map' },
          { href: '/dashboard/page-optimizer/audits', label: 'Seiten-Audits' },
        ],
      },
      {
        heading: 'Optimierung',
        items: [
          { href: '/dashboard/page-optimizer/hypotheses', label: 'Hypothesen' },
          { href: '/dashboard/page-optimizer/proposals', label: 'Proposals' },
        ],
      },
    ],
  },
  {
    kind: 'flat',
    label: 'Research & Tests',
    items: [
      { href: '/dashboard/marketing/research', label: 'Research Library' },
      { href: '/dashboard/marketing/social-market', label: 'Social-Marktdaten' },
      { href: '/dashboard/marketing/hypotheses', label: 'Strategy-Hypothesen' },
      { href: '/dashboard/marketing/claims', label: 'Claim Registry' },
      { href: '/dashboard/marketing/experiments', label: 'Experimente' },
      { href: '/dashboard/marketing/cro', label: 'CRO Findings' },
    ],
  },
  {
    kind: 'sectioned',
    label: 'Content',
    sections: [
      {
        heading: 'Favorites',
        items: [
          { href: '/dashboard/content-machine/rahmenplan', label: 'Rahmenplan' },
          { href: '/dashboard/content/strategie-freigabe', label: 'Strategie-Freigabe' },
          { href: '/dashboard/content/monatsplan', label: 'Monatsplan' },
          { href: '/dashboard/content-machine/wochenkette', label: 'Wochenkette' },
          { href: '/dashboard/content/calendar', label: 'Content Calendar' },
          { href: '/dashboard/content/pipeline', label: 'Content Pipeline' },
          { href: '/dashboard/content/freigaben', label: 'Freigaben' },
          { href: '/dashboard/content/freigabe', label: 'Post-Freigabe' },
          { href: '/dashboard/content/reel-freigabe', label: 'Reel-Freigabe' },
          { href: '/dashboard/content/musik', label: 'Reel-Musik' },
        ],
      },
      {
        heading: 'Creatives Production',
        items: [
          { href: '/dashboard/mediendatenbank', label: 'Mediendatenbank' },
          { href: '/dashboard/mediendatenbank/sichtung', label: 'Sichtung & Ranking' },
          { href: '/dashboard/mediendatenbank/video-vergleich', label: 'Video-Vergleich' },
          { href: '/dashboard/mediendatenbank/videos', label: 'Video-Sichtung' },
          { href: '/dashboard/creatives', label: 'Creatives' },
          { href: '/dashboard/content/creative-preview', label: 'Creative Live Preview' },
          { href: '/dashboard/content-machine/layouts', label: 'Layouts' },
          { href: '/dashboard/vorlagen', label: 'Vorlagen' },
        ],
      },
      {
        heading: 'Video Production',
        items: [
          { href: '/dashboard/scripts', label: 'Skripte' },
          { href: '/dashboard/voiceovers', label: 'VoiceOver Recorder' },
          { href: '/dashboard/mediendatenbank/library', label: 'Cutscenes' },
          { href: '/dashboard/mediendatenbank/library/final', label: 'Final Library' },
          { href: '/dashboard/reel-production', label: 'Video Production' },
          { href: '/dashboard/video-presets', label: 'Render Presets' },
        ],
      },
      {
        heading: 'Outreach',
        items: [
          { href: '/dashboard/stein-finder', label: 'Stein-Finder' },
          { href: '/dashboard/steinfinder', label: 'Umfrage-Ergebnisse' },
          { href: '/dashboard/interview', label: 'Live Chat' },
        ],
      },
      {
        heading: 'Blog & Content',
        items: [
          { href: '/dashboard/content/calendar', label: 'Content' },
          { href: '/dashboard/posts/ideas', label: 'Ideen' },
          { href: '/dashboard/creatives', label: 'Creatives' },
          { href: '/dashboard/blog', label: 'Blog' },
          { href: '/dashboard/blog/reviews', label: 'Blog-Reviews' },
          { href: '/dashboard/blog/linking', label: 'Blog-Linking' },
          { href: '/dashboard/content/atoms', label: 'Content-Atoms' },
          { href: '/dashboard/content/stories', label: 'Story-Mining' },
          { href: '/dashboard/content/performance', label: 'Content-Scorecard' },
          { href: '/dashboard/content/trends', label: 'Pinterest Trends' },
          { href: '/dashboard/bauplaner', label: 'Bauplaner' },
        ],
      },
      {
        heading: 'Machine',
        items: [
          { href: '/dashboard/marketing-machine/content-learnings', label: 'Learning-Freigaben' },
          { href: '/dashboard/content-machine/rahmenplan', label: 'Rahmenplan' },
          { href: '/dashboard/content-machine/strategy', label: 'Gesamtstrategie' },
          { href: '/dashboard/content-machine/strategy/channels', label: 'Kanalstrategien' },
          { href: '/dashboard/content-machine/templates', label: 'Templates' },
          { href: '/dashboard/content-machine/layouts', label: 'Layouts' },
          { href: '/dashboard/content-machine/checkpoints', label: 'Checkpoints' },
          { href: '/dashboard/content-machine/pipeline', label: 'Pipeline' },
          { href: '/dashboard/content-machine/ads-proposals', label: 'Ads Proposals' },
          { href: '/dashboard/content-machine/audit', label: 'Audit Log' },
          { href: '/dashboard/research/hypotheses', label: 'Research-Hypothesen' },
        ],
      },
      {
        heading: 'Video',
        items: [
          { href: '/dashboard/reel-production', label: 'Video Production' },
        ],
      },
    ],
  },
  {
    kind: 'sectioned',
    label: 'E-Mail',
    sections: [
      {
        heading: 'Flows & Automationen',
        items: [
          { href: '/dashboard/mail/flows', label: 'Flows' },
          { href: '/dashboard/mail/layouts', label: 'E-Mail-Layouts' },
        ],
      },
      {
        heading: 'Kampagnen',
        items: [
          { href: '/dashboard/mail', label: 'Kampagnen' },
          { href: '/dashboard/mail/bulk-send', label: 'Bulk Send Ops' },
        ],
      },
      {
        heading: 'Kontakte & Listen',
        items: [
          { href: '/dashboard/mail/lists', label: 'Listen' },
          { href: '/dashboard/mail/segments', label: 'Segmente' },
          { href: '/dashboard/leads', label: 'Leads' },
        ],
      },
      {
        heading: 'Betrieb & Analyse',
        items: [
          { href: '/dashboard/mail/analytics', label: 'Analytics' },
          { href: '/dashboard/mail/deliverability', label: 'Deliverability' },
          { href: '/dashboard/mail/ops', label: 'Ops-Status' },
        ],
      },
      {
        heading: 'B2B',
        items: [
          { href: '/dashboard/kita-mail', label: 'Kita Mail' },
          { href: '/dashboard/kita-mail/varianten', label: 'Kita Varianten' },
        ],
      },
    ],
  },
  {
    kind: 'flat',
    label: 'Planung',
    items: [
      { href: '/dashboard/planung', label: 'Heute' },
      { href: '/dashboard/planung/aufgaben', label: 'Aufgaben' },
      { href: '/dashboard/planung/projekte', label: 'Projekte' },
      { href: '/dashboard/planung/termine', label: 'Termine' },
      { href: '/dashboard/planung/woche', label: 'Wochenplan' },
      { href: '/dashboard/planung/arbeitslog', label: 'Aktivitätslog' },
      { href: '/dashboard/planung/produktion', label: 'Produktion' },
      { href: '/dashboard/planung/reviews', label: 'Reviews' },
      { href: '/dashboard/planung/wissen', label: 'Wissen' },
      { href: '/dashboard/planung/system', label: 'System' },
    ],
  },
  {
    kind: 'sectioned',
    label: 'CRM',
    sections: [
      {
        heading: 'Kommunikation',
        items: [
          { href: '/dashboard/kommunikation', label: 'Nachrichten' },
          { href: '/dashboard/kommunikation/kontakte', label: 'Kontakte' },
          { href: '/dashboard/kommunikation/cases', label: 'Cases' },
          { href: '/dashboard/kommunikation/zusammenfassungsprompt', label: 'Zusammenfassungs-Prompt' },
          { href: '/dashboard/kommunikation/reviews', label: 'Kunden-Reviews' },
        ],
      },
      {
        heading: 'Kooperationen',
        items: [
          { href: '/dashboard/kooperationen/influencer', label: 'Influencer' },
          { href: '/dashboard/kooperationen/creators', label: 'Creators CRM' },
          { href: '/dashboard/kooperationen/bubble', label: 'Bubble Analyse' },
        ],
      },
    ],
  },
  {
    kind: 'sectioned',
    label: 'System',
    sections: [
      {
        heading: 'KI & Build',
        items: [

          { href: '/dashboard/agenten/karte', label: 'Agenten-Karte' },
          { href: '/dashboard/agents/skills', label: 'Skills' },
          { href: '/dashboard/roadmap', label: 'Roadmap' },
          { href: '/dashboard/roadmap/projekte', label: 'Roadmap-Projekte' },
          { href: '/dashboard/roadmap/friktion', label: 'Roadmap-Friktion' },
          { href: '/dashboard/agent-tasks', label: 'Agent Tasks' },
          { href: '/dashboard/agent-jobs', label: 'Agent-Runs' },
          { href: '/dashboard/agent-reports', label: 'Agent Reports' },
          { href: '/dashboard/cron-drops', label: 'Cron-Drops' },
          { href: '/dashboard/claude', label: 'Claude' },
          { href: '/dashboard/opencode', label: 'OpenCode' },
          { href: '/dashboard/builder/strategy', label: 'Stratege' },
          { href: '/dashboard/build-manager', label: 'Build-Manager' },
          { href: '/dashboard/artemis', label: 'Artemis II' },
        ],
      },
      {
        heading: 'Werkzeuge',
        items: [
          { href: '/system-explainer.html', label: 'Systemüberblick' },
          { href: '/dashboard/wissen', label: 'Wissens-Navigation' },
          { href: '/dashboard/minecraft-wiki', label: 'Minecraft-Wiki' },
          { href: '/dashboard/approvals', label: 'Approvals' },
          { href: '/dashboard/inbox', label: 'Inbox' },
          { href: '/dashboard/upload', label: 'Upload' },
          { href: '/dashboard/obsidian-upload', label: 'Obsidian' },
          { href: '/dashboard/vault', label: 'Vault' },
          { href: '/dashboard/export', label: 'Kontext-Export' },
          { href: '/dashboard/dateibrowser', label: 'Dateibrowser' },
          { href: '/dashboard/db-health', label: 'DB Health' },
          { href: '/dashboard/verbindungen', label: 'Verbindungen' },
          { href: '/dashboard/users', label: 'Benutzer' },
          { href: '/dashboard/darstellung', label: 'Darstellung' },
          { href: '/dashboard/design-tokens', label: 'Design Tokens' },
          { href: '/dashboard/ui-seiten', label: 'UI-Seiten' },
          { href: '/dashboard/minecraft', label: 'Minecraft' },
        ],
      },
    ],
  },
];

// Flatten the nav tree into grouped picker rows (group label + heading + items)
// for the nav-template editor's checkbox UI.
export type PickerGroup = { group: string; heading: string | null; items: NavItem[] };

export function navPickerGroups(): PickerGroup[] {
  const out: PickerGroup[] = [];
  for (const entry of nav) {
    if (entry.kind === 'link') {
      out.push({ group: entry.label, heading: null, items: [{ href: entry.href, label: entry.label }] });
    } else if (entry.kind === 'flat') {
      out.push({ group: entry.label, heading: null, items: entry.items });
    } else {
      for (const section of entry.sections) {
        out.push({ group: entry.label, heading: section.heading, items: section.items });
      }
    }
  }
  return out;
}
